import AttachFileIcon from '@mui/icons-material/AttachFile'
import CloseIcon from '@mui/icons-material/Close'
import {
    Box,
    Button,
    FormHelperText,
    Grid,
    IconButton,
    Paper,
    TextField,
    Typography,
} from '@mui/material'
import PropTypes from 'prop-types'
import React, { useEffect, useState } from 'react'
import ApprovalProbability from '../../components/ApprovalProbability'
import SingleSelect from '../../components/SingleSelect'
import WhatsAppMessagePreview from '../../components/WhatsAppMessagePreview'
import {
    WA_HEADER_TYPES,
    WA_LIMITS,
    WA_MEDIA,
    emptyButton,
    mediaAccept,
    mediaFileProblem,
} from '../../helpers/whatsappTemplate'
import { ICON, TEXT, cardSx } from '../../theme/tokens'

// The button kinds the AWS registration builds (`_build_components`): a URL
// or phone number carries its target in `button_data`.
const WA_BUTTON_TYPES = [
    { id: 1, label: 'None', value: '' },
    { id: 2, label: 'URL', value: 'URL' },
    { id: 3, label: 'Quick Reply', value: 'QUICK_REPLY' },
    { id: 4, label: 'Phone number', value: 'PHONE_NUMBER' },
]

/** An overline label above a part of the content card. */
const PartLabel = ({ children }) => (
    <Typography
        variant="overline"
        component="div"
        sx={{ color: TEXT.muted, lineHeight: 2, mb: 0.5 }}
    >
        {children}
    </Typography>
)

PartLabel.propTypes = { children: PropTypes.node.isRequired }

/** A field's hint on the left and its "n/limit" counter on the right. */
const Counted = ({ hint, value, limit }) => (
    <Box
        component="span"
        sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}
    >
        <span>{hint}</span>
        <span>
            {String(value ?? '').length}/{limit}
        </span>
    </Box>
)

Counted.propTypes = {
    hint: PropTypes.node,
    value: PropTypes.string,
    limit: PropTypes.number.isRequired,
}

/**
 * The Message Content card of a WhatsApp template: the body (at most 1024
 * characters, with a counter), an example per {{variable}}, the approval
 * probability check for Utility templates, the header (none, text, or an
 * image, video or document file), the footer, the buttons, and a live
 * preview of the message.
 *
 * It holds no template state of its own: the form keeps the body and the
 * draft (helpers/whatsappTemplate emptyWhatsAppDraft) and builds the request
 * from them. `marks` are what the last submit found wrong, to outline those
 * fields; each edit reports which mark it may clear. A media file is checked
 * (type, extension, size) as soon as it is chosen, and a refused one is not
 * kept. The preview shows a chosen file through an object URL, released when
 * the file changes or the card goes.
 */
const WhatsAppContent = ({
    body,
    onBodyChange,
    draft,
    onDraftChange,
    variables,
    category,
    marks,
}) => {
    const [mediaMessage, setMediaMessage] = useState('')
    const [mediaUrl, setMediaUrl] = useState('')

    useEffect(() => {
        if (!draft.mediaFile || typeof URL.createObjectURL !== 'function') {
            setMediaUrl('')
            return undefined
        }
        const url = URL.createObjectURL(draft.mediaFile)
        setMediaUrl(url)
        return () => URL.revokeObjectURL?.(url)
    }, [draft.mediaFile])

    const media = WA_MEDIA[draft.headerType]

    const setHeaderType = (headerType) => {
        setMediaMessage('')
        onDraftChange(
            {
                headerType,
                // A chosen image is no video: a new kind needs a new file.
                mediaFile:
                    headerType === draft.headerType ? draft.mediaFile : null,
            },
            { header: true }
        )
    }

    const chooseMedia = (event) => {
        const file = event.target.files?.[0]
        // So choosing the same file again still fires `change`.
        event.target.value = ''
        if (!file) return
        const problem = mediaFileProblem(draft.headerType, file)
        setMediaMessage(problem)
        if (!problem) onDraftChange({ mediaFile: file }, { header: true })
    }

    const setButton = (index, field, value) =>
        onDraftChange(
            {
                buttons: draft.buttons.map((b, i) =>
                    i === index ? { ...b, [field]: value } : b
                ),
            },
            { buttonRow: index }
        )

    const removeButton = (index) =>
        onDraftChange(
            { buttons: draft.buttons.filter((_, i) => i !== index) },
            { buttonRows: true }
        )

    const headerProblem = mediaMessage || marks.header

    return (
        <Paper elevation={0} sx={{ ...cardSx, p: 3, mb: 3 }}>
            <PartLabel>Message Content</PartLabel>
            <Grid container spacing={3}>
                <Grid item xs={12} md={7}>
                    <PartLabel>Template Body</PartLabel>
                    <TextField
                        multiline
                        rows={8}
                        placeholder={
                            'Write your WhatsApp message here.\nUse {{name}}, {{class_time}}, etc. for dynamic variables.\n\nExample:\nHi {{name}}, your session is confirmed for {{class_date}} at {{class_time}}.'
                        }
                        value={body}
                        onChange={(event) => onBodyChange(event.target.value)}
                        fullWidth
                        inputProps={{
                            'aria-label': 'WhatsApp template body',
                            maxLength: WA_LIMITS.body,
                        }}
                        helperText={
                            <Counted
                                hint="Use {{variable_name}} placeholders; each needs an example value for review by WhatsApp"
                                value={body}
                                limit={WA_LIMITS.body}
                            />
                        }
                    />

                    {variables.length > 0 && (
                        <Box sx={{ mt: 2 }}>
                            <PartLabel>
                                Variable examples ({variables.length})
                            </PartLabel>
                            <Box
                                sx={{
                                    display: 'grid',
                                    gridTemplateColumns:
                                        'repeat(auto-fill, minmax(180px, 1fr))',
                                    gap: 1.5,
                                }}
                            >
                                {variables.map((v) => (
                                    <TextField
                                        key={v}
                                        required
                                        label={`{{${v}}}`}
                                        value={draft.examples[v] ?? ''}
                                        onChange={(e) =>
                                            onDraftChange(
                                                {
                                                    examples: {
                                                        ...draft.examples,
                                                        [v]: e.target.value,
                                                    },
                                                },
                                                {}
                                            )
                                        }
                                        fullWidth
                                    />
                                ))}
                            </Box>
                        </Box>
                    )}

                    {category === 'UTILITY' && (
                        <ApprovalProbability
                            body={body}
                            variables={variables}
                            examples={draft.examples}
                            onUseRevised={onBodyChange}
                        />
                    )}

                    <Box sx={{ mt: 2 }}>
                        <PartLabel>Header (optional)</PartLabel>
                        <Box
                            sx={{
                                display: 'flex',
                                gap: 1.5,
                                alignItems: 'flex-start',
                                flexWrap: 'wrap',
                            }}
                        >
                            <Box sx={{ width: 160 }}>
                                <SingleSelect
                                    label="Header"
                                    options={WA_HEADER_TYPES}
                                    optionLabelText="label"
                                    optionValueText="value"
                                    value={draft.headerType}
                                    handleChange={setHeaderType}
                                    error={Boolean(headerProblem)}
                                />
                            </Box>
                            {draft.headerType === 'TEXT' && (
                                <TextField
                                    label="Header text"
                                    value={draft.headerText}
                                    error={Boolean(headerProblem)}
                                    onChange={(e) =>
                                        onDraftChange(
                                            { headerText: e.target.value },
                                            { header: true }
                                        )
                                    }
                                    inputProps={{
                                        maxLength: WA_LIMITS.headerText,
                                    }}
                                    helperText={
                                        <Counted
                                            value={draft.headerText}
                                            limit={WA_LIMITS.headerText}
                                        />
                                    }
                                    sx={{ flex: 1, minWidth: 200 }}
                                />
                            )}
                            {media && (
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 1,
                                        flex: 1,
                                        minWidth: 200,
                                        flexWrap: 'wrap',
                                    }}
                                >
                                    <Button
                                        variant="outlined"
                                        component="label"
                                        startIcon={<AttachFileIcon />}
                                    >
                                        {draft.mediaFile
                                            ? `Choose another ${media.label}`
                                            : `Choose the ${media.label}`}
                                        <input
                                            hidden
                                            type="file"
                                            accept={mediaAccept(
                                                draft.headerType
                                            )}
                                            aria-label={`Header ${media.label} file`}
                                            onChange={chooseMedia}
                                        />
                                    </Button>
                                    {draft.mediaFile && (
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 0.5,
                                                minWidth: 0,
                                            }}
                                        >
                                            <Typography
                                                variant="body2"
                                                sx={{
                                                    color: TEXT.secondary,
                                                    overflowWrap: 'anywhere',
                                                }}
                                            >
                                                {draft.mediaFile.name}
                                            </Typography>
                                            <Button
                                                size="small"
                                                onClick={() => {
                                                    setMediaMessage('')
                                                    onDraftChange(
                                                        { mediaFile: null },
                                                        { header: true }
                                                    )
                                                }}
                                            >
                                                Remove
                                            </Button>
                                        </Box>
                                    )}
                                </Box>
                            )}
                        </Box>
                        {media && !headerProblem && (
                            <FormHelperText sx={{ mx: 0 }}>
                                {media.kind.charAt(0).toUpperCase() +
                                    media.kind.slice(1)}
                                , up to {media.maxBytes / (1024 * 1024)} MB. It
                                is uploaded when you submit.
                            </FormHelperText>
                        )}
                        {headerProblem && (
                            <FormHelperText error sx={{ mx: 0 }}>
                                {headerProblem}
                            </FormHelperText>
                        )}
                    </Box>

                    <Box sx={{ mt: 2 }}>
                        <PartLabel>Footer (optional)</PartLabel>
                        <TextField
                            label="Footer text"
                            value={draft.footerText}
                            onChange={(e) =>
                                onDraftChange(
                                    { footerText: e.target.value },
                                    {}
                                )
                            }
                            inputProps={{ maxLength: WA_LIMITS.footerText }}
                            helperText={
                                <Counted
                                    value={draft.footerText}
                                    limit={WA_LIMITS.footerText}
                                />
                            }
                            fullWidth
                        />
                    </Box>

                    <Box sx={{ mt: 2 }}>
                        <PartLabel>Buttons (optional)</PartLabel>
                        {draft.buttons.map((btn, i) => {
                            const problem = marks.buttonRows[i]
                            return (
                                <Box key={i} sx={{ mb: 1.5 }}>
                                    <Box
                                        data-testid={`wa-button-row-${i + 1}`}
                                        data-invalid={
                                            problem ? 'true' : 'false'
                                        }
                                        sx={{
                                            display: 'flex',
                                            gap: 1.5,
                                            alignItems: 'flex-start',
                                        }}
                                    >
                                        <Box sx={{ minWidth: 140 }}>
                                            <SingleSelect
                                                label={`Button ${i + 1}`}
                                                options={WA_BUTTON_TYPES}
                                                optionLabelText="label"
                                                optionValueText="value"
                                                value={btn.button_type}
                                                error={Boolean(problem)}
                                                handleChange={(v) =>
                                                    setButton(
                                                        i,
                                                        'button_type',
                                                        v
                                                    )
                                                }
                                            />
                                        </Box>
                                        {btn.button_type && (
                                            <TextField
                                                label="Button text"
                                                value={btn.button_text}
                                                error={Boolean(problem)}
                                                onChange={(e) =>
                                                    setButton(
                                                        i,
                                                        'button_text',
                                                        e.target.value
                                                    )
                                                }
                                                fullWidth
                                            />
                                        )}
                                        {btn.button_type === 'PHONE_NUMBER' && (
                                            <TextField
                                                label="Phone number"
                                                placeholder="+919876543210"
                                                helperText="With the country code"
                                                value={btn.button_data}
                                                error={Boolean(problem)}
                                                onChange={(e) =>
                                                    setButton(
                                                        i,
                                                        'button_data',
                                                        e.target.value
                                                    )
                                                }
                                                fullWidth
                                            />
                                        )}
                                        {btn.button_type === 'URL' && (
                                            <TextField
                                                label="URL"
                                                placeholder="https://…"
                                                value={btn.button_data}
                                                error={Boolean(problem)}
                                                onChange={(e) =>
                                                    setButton(
                                                        i,
                                                        'button_data',
                                                        e.target.value
                                                    )
                                                }
                                                fullWidth
                                            />
                                        )}
                                        <IconButton
                                            aria-label={`Remove button ${
                                                i + 1
                                            }`}
                                            onClick={() => removeButton(i)}
                                            sx={{ color: TEXT.muted, mt: 0.25 }}
                                        >
                                            <CloseIcon
                                                sx={{ fontSize: ICON.md }}
                                            />
                                        </IconButton>
                                    </Box>
                                    {problem && (
                                        <FormHelperText error sx={{ mx: 0 }}>
                                            {problem}
                                        </FormHelperText>
                                    )}
                                </Box>
                            )
                        })}
                        {draft.buttons.length < WA_LIMITS.buttons && (
                            <Button
                                variant="outlined"
                                onClick={() =>
                                    onDraftChange(
                                        {
                                            buttons: [
                                                ...draft.buttons,
                                                emptyButton(),
                                            ],
                                        },
                                        {}
                                    )
                                }
                            >
                                Add a button
                            </Button>
                        )}
                    </Box>
                </Grid>
                <Grid item xs={12} md={5}>
                    <PartLabel>Preview</PartLabel>
                    <WhatsAppMessagePreview
                        headerText={
                            draft.headerType === 'TEXT' ? draft.headerText : ''
                        }
                        media={
                            media && draft.mediaFile
                                ? {
                                      kind: draft.headerType,
                                      url: mediaUrl,
                                      fileName: draft.mediaFile.name,
                                  }
                                : null
                        }
                        body={body}
                        footer={draft.footerText}
                        buttons={draft.buttons}
                    />
                </Grid>
            </Grid>
        </Paper>
    )
}

WhatsAppContent.propTypes = {
    body: PropTypes.string.isRequired,
    // Called with the new body text.
    onBodyChange: PropTypes.func.isRequired,
    // helpers/whatsappTemplate emptyWhatsAppDraft's shape.
    draft: PropTypes.shape({
        headerType: PropTypes.string,
        headerText: PropTypes.string,
        mediaFile: PropTypes.object,
        footerText: PropTypes.string,
        buttons: PropTypes.array,
        examples: PropTypes.object,
    }).isRequired,
    // Called with (patch, cleared): the fields that changed, and the marks
    // that edit may clear ({header}, {buttonRow: index}, {buttonRows}).
    onDraftChange: PropTypes.func.isRequired,
    // The body's {{variables}}, in order.
    variables: PropTypes.arrayOf(PropTypes.string).isRequired,
    // The chosen category; Utility shows the approval check.
    category: PropTypes.string,
    // What the last submit found wrong: {header: string, buttonRows: {index: string}}.
    marks: PropTypes.shape({
        header: PropTypes.string,
        buttonRows: PropTypes.object,
    }).isRequired,
}

export default WhatsAppContent
